// src/components/Common/Layout.tsx
import React, { ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Activity, Users, Key, Settings, LogOut, Building2 } from 'lucide-react';
import './Layout.css';

interface LayoutProps {
  children: ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isSystemAdmin } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // SystemAdmin sees Tenants management
  // Tenant users see Consumers, API Keys, Services
  const menuItems = isSystemAdmin()
    ? [
        { path: '/', icon: Activity, label: 'Dashboard' },
        { path: '/tenants', icon: Building2, label: 'Tenants' },
      ]
    : [
        { path: '/', icon: Activity, label: 'Dashboard' },
        { path: '/consumers', icon: Users, label: 'Consumers' },
        { path: '/api-keys', icon: Key, label: 'API Keys' },
        { path: '/services', icon: Settings, label: 'API Services' },
      ];

  return (
    <div className="layout">
      <header className="header">
        <h1>API Control Center</h1>
        <div className="header-right">
          {user?.tenantName && (
            <span className="tenant-name">{user.tenantName}</span>
          )}
          <span className="username">
            {user?.displayName || user?.username}
            <span className="user-role">{user?.role}</span>
          </span>
          <button onClick={handleLogout} className="logout-btn">
            <LogOut size={18} />
            Logout
          </button>
        </div>
      </header>

      <div className="layout-body">
        <aside className="sidebar">
          <nav>
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
                >
                  <Icon size={20} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </aside>

        <main className="main-content">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
